/**
 * Meta Ads module — reuse app theme tokens (see apps/dashboard/src/index.css).
 * Use Tailwind classes: primary, brand-cyan, brand-purple, ring, border, card, etc.
 */

import { cn } from "@/lib/utils";

/** Focus ring on dark inputs — matches WidgetConfigurator / StatCard */
export const metaAdsInputFocusClass =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan/50";

/** Primary solid CTA — same pattern as Billing / EmptyBusinessState */
export const metaAdsPrimaryButtonClass =
  "bg-primary text-primary-foreground shadow-lg shadow-primary/25 transition hover:bg-primary/90";

/** Text link / accent — same as Link in Bio & widgets */
export const metaAdsLinkClass =
  "text-brand-cyan transition hover:text-brand-cyan/80";

/** Brand gradient CTA (Launch campaign) — uses `.bg-brand-gradient` from index.css */
export const metaAdsGradientCtaClass =
  "bg-brand-gradient text-primary-foreground shadow-lg transition hover:opacity-95";

/** Title word gradient (Promote…) */
export const metaAdsTitleGradientClass =
  "bg-gradient-to-r from-brand-cyan to-brand-purple bg-clip-text text-transparent";

/** 1px cyan → purple frame (no outer shadow) — hub hero visual, small widgets */
export const metaAdsGradientBorderOnlyClass =
  "rounded-2xl bg-gradient-to-r from-brand-cyan to-brand-purple p-px";

/** Connect card — same frame as border-only + elevation */
export const metaAdsGradientCardEdgeClass =
  "rounded-2xl bg-gradient-to-r from-brand-cyan to-brand-purple p-px shadow-xl shadow-primary/15";

/** Outline / secondary CTA (Continue with Meta, ghost buttons) */
export const metaAdsOutlineCtaClass =
  "border border-border bg-input text-foreground transition hover:bg-muted";

/**
 * Select + native textarea only — same surface as `Input` variant="dark" (BusinessCreationForm).
 * @see components/ui/UIElements/Input — dark: border-gray-600 bg-gray-800, focus:ring-blue-500
 */
export const metaAdsSelectClass = cn(
  "custom-select flex h-11 w-full cursor-pointer rounded-md border border-gray-600 bg-gray-800 px-3 text-base text-white",
  "shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
);

export const metaAdsTextareaClass = cn(
  "min-h-[120px] w-full resize-y rounded-md border border-gray-600 bg-gray-800 px-3 py-3 text-base text-white placeholder-gray-400 shadow-sm",
  "transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
);

/** Section card surface (forms) */
export const metaAdsFormSectionClass =
  "rounded-2xl border border-border bg-card p-5";

export const metaAdsFormSectionLabelClass =
  "text-xs font-semibold uppercase tracking-wide text-muted-foreground";

/** Field labels — matches store create form (BusinessCreationForm) */
export const metaAdsFormFieldLabelClass = "text-sm font-medium text-gray-300";

/** Shared red palette — outline / ghost deletes (Danger Zone vs table icon actions) */
const metaAdsDangerZoneOutlineSurfaceClass = cn(
  "border-red-500/55 bg-transparent text-red-400",
  "hover:border-red-400/65 hover:bg-red-950/45 hover:text-red-300",
);

/** Icon-only destructive control (toolbar row delete) — same reds as Danger Zone outline */
export const metaAdsDestructiveIconButtonClass = cn(
  "focus-visible:ring-brand-cyan/35 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition",
  "focus-visible:outline-none focus-visible:ring-2",
  "border-transparent bg-transparent text-red-400",
  "hover:border-red-400/65 hover:bg-red-950/45 hover:text-red-300",
  "disabled:cursor-not-allowed disabled:opacity-45",
);

/**
 * Full-width Danger Zone delete outline — GitHub-style box CTA (“Delete campaign”).
 * Same reds as metaAdsDestructiveIconButtonClass; rests with visible frame border.
 */
export const metaAdsDangerZoneDeleteButtonClass = cn(
  "focus-visible:ring-brand-cyan/35 inline-flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition outline-none focus-visible:ring-2",
  metaAdsDangerZoneOutlineSurfaceClass,
  "disabled:cursor-not-allowed disabled:opacity-45 sm:min-w-[11rem]",
);

/** Solid destructive confirm — keep in sync with `DeleteConfirmationModal` confirm primary. Use in Meta Ads only (do not wire the modal to this export). */
export const metaAdsDestructiveSolidConfirmButtonClass =
  "rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50";
